import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { BranchFormRule, BranchFormSchema } from "@factory/form/form.rule";
import { useFormDefaultBranch } from "@factory/form/form-default-branch.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateBranch, 
  useQyGetBranchById, 
  useQyDeleteBranch 
} from "@factory/query/branch.query";
import { GetResponseBranchModel } from "@factory/model/query-model-branch";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditBranch() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultBranch();
  const navigate = useNavigate();
  const { mapUpdateBranch, mapDeleteBranch } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { branchId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Branch
  const handleGetApiSuccess = (data: GetResponseBranchModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("head", responseData?.head || "");
      setValue("tenant", responseData?.tenant || "");
  };
  const {
    data: branchResponse,
    isLoading,
    isError: branchError,
  } = useQyGetBranchById(branchId || "", handleGetApiSuccess);
  const branchData = branchResponse?.data?.[0];

  // API UPDATE Branch
  const handleUpdateApiSuccess = () => {
    showSuccess("Branch updated successfully");
  };
  const { mutate: updateBranch, isLoading: isEditLoading } = useQyUpdateBranch(
    handleUpdateApiSuccess
  );

  // API DELETE Branch
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Branch deleted successfully");
  };
  const { mutate: deleteBranch } = useQyDeleteBranch(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<BranchFormSchema>({
    defaultValues: getDefault(branchData),
    resolver: zodResolver(BranchFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: BranchFormSchema) => {
    if (!branchData || !branchData.code) {
      throw new Error("No branch data");
    }

    const formData = mapUpdateBranch(form, branchData.code);
    updateBranch(formData);
  };
  const handleValidateError = (err: FieldErrors<BranchFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!branchData || !branchData.code) {
      throw new Error("No branch data found");
    }

    const form = mapDeleteBranch(branchData.code);
    deleteBranch(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    branchId,
    branchData,
    isLoading,
    branchError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}